/**
 * A shuffle that is stable for a given seed.
 *
 * Every shell that hides an order needs the same two properties, and they pull
 * against each other. It has to be shuffled, or the check is winnable without
 * reading it. And it has to be *the same* shuffle every render, because these
 * components re-render on every tap — a child who has just answered wrong and
 * is re-reading the options must not have them move under them mid-thought.
 *
 * So the arrangement is a pure function of the seed rather than of chance, and
 * callers pass the check's own prompt. Not cryptographic and not trying to be:
 * the thing being hidden is a quiz answer from a seven-year-old, and the
 * property that matters is "not always position one".
 *
 * Consequence worth knowing: the same check shuffles the same way every time a
 * child re-practises it, so a star relit ten times sits in one position all ten.
 * That is the deliberate trade — stability during an attempt beats variety
 * across them, because options moving under a child mid-answer is a bug they
 * can feel and a fixed position is one they have to work to notice.
 */
export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const out = [...items];

  // FNV-1a. Any avalanche-y string hash does; this one is short and mixes the
  // tail of a string as hard as the head, which matters because check prompts
  // differ from each other late ("Why do cats...", "Why do dogs...").
  let state = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    state ^= seed.charCodeAt(i);
    state = Math.imul(state, 0x01000193) >>> 0;
  }
  state = state || 0x9e3779b9;

  /*
    xorshift32, read from the TOP bits.

    Both halves of that are load-bearing, and the version this replaced got both
    wrong. It was an LCG — `hash * 1103515245 + 12345` — indexed with
    `hash % (i + 1)`, which takes the lowest bits. The low bits of an LCG are
    the classic trap: the bottom bit alternates on a fixed cycle no matter what
    you seed it with, so `% 2` on the last iteration was very nearly a constant.

    Measured, with the correct answer first and 300 different prompts, that
    shuffle put it in position 1 or 2 and **never once in position 0**. Which is
    not a shuffle — it is the original bug wearing a hat. A child who learned
    "it is never the top one" has learned something as useful as "it is always
    the top one", and faster.
  */
  const next = (): number => {
    state ^= state << 13;
    state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state;
  };

  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor((next() / 0x100000000) * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }

  return out;
}
